import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { dictionaries } from '../i18n/dictionaries';

const LanguageContext = createContext(null);
const KEY = 'axicor_lang';

/** Arabic is served from /ar/... paths; react-router runs with a matching basename. */
export const AR_PREFIX = '/ar';

export const langFromPath = (pathname = window.location.pathname) =>
  pathname === AR_PREFIX || pathname.startsWith(`${AR_PREFIX}/`) ? 'ar' : 'en';

export const stripLangPrefix = (pathname = window.location.pathname) =>
  langFromPath(pathname) === 'ar' ? pathname.slice(AR_PREFIX.length) || '/' : pathname;

export const useLanguage = () => useContext(LanguageContext);

/** t('grade.chemical') with a graceful fallback to English then to the key itself. */
const resolve = (dict, path) => path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), dict);

export function LanguageProvider({ children }) {
  const [lang] = useState(langFromPath);
  const isRtl = lang === 'ar';

  useEffect(() => {
    localStorage.setItem(KEY, lang);
    const html = document.documentElement;
    html.setAttribute('lang', lang);
    html.setAttribute('dir', dictionaries[lang].dir);
  }, [lang]);

  /** Switching language is a real navigation so the router basename changes with it. */
  const setLang = useCallback((next) => {
    if (!dictionaries[next] || next === lang) return;
    localStorage.setItem(KEY, next);
    const bare = stripLangPrefix() + window.location.search + window.location.hash;
    window.location.assign(next === 'ar' ? `${AR_PREFIX}${bare === '/' ? '' : bare}` || AR_PREFIX : bare);
  }, [lang]);

  const t = useCallback((path, vars) => {
    const value = resolve(dictionaries[lang], path) ?? resolve(dictionaries.en, path) ?? path;
    if (typeof value !== 'string' || !vars) return value;
    return value.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, isRtl }), [lang, setLang, t, isRtl]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
